import type { PracticeRequirementsDto } from 'api/credentialing/types';

export type PayerStatus = 'open' | 'planned';
export type PayerCoverage = 'all' | string[];

export interface Payer {
    key: string;
    name: string;
    type: 'national' | 'regional' | 'partial';
    coverage: PayerCoverage;
    status: PayerStatus;
    month: string;
    cat?: string;
}

export type NpiHistory = '' | 'no' | 'direct';
export type NpiNoReason = '' | 'never' | 'company' | 'hospital';

export type CredentialingStatus =
    | 'packet_review'
    | 'packet_incomplete'
    | 'validated'
    | 'submitting_to_payer'
    | 'submitted_to_payer'
    | 'payer_review'
    | 'payer_rfi'
    | 'signature_needed'
    | 'approved'
    | 'billing_setup'
    | 'billing_active'
    | 'disapproved'
    | 'paused'
    | 'withdrawn'
    | 'deposit_failed'
    | 'payment_pending'
    | 'panel_closed'
    | 'action_needed';

export type StatusBucket = 'action' | 'inprogress' | 'approved' | 'notapproved' | 'panelclosed';

export enum EUserPlatformStatus {
    FLASH = 'flash',
    PRIORITY = 'priority',
    STANDARD = 'standard',
}
export type UserPlatformStatus = `${EUserPlatformStatus}`;

export enum ECoverStatesMode {
    SINGLE = 'single',
    MULTI = 'multi',
    NOCOVERAGE = 'nocoverage',
}

// 计费 NPI 类型：与后端 CREDENTIALING_BILLING_NPI_TYPE 记录值 / overview billingNpiType.value 保持一致（大写）
// solo=INDIVIDUAL_NPI，group=PRACTICE_NPI。全站统一使用该枚举，勿再散落裸字符串。
export enum ENPIType {
    PRACTICE_NPI = 'PRACTICE_NPI',
    INDIVIDUAL_NPI = 'INDIVIDUAL_NPI',
}

export enum EPracticeType {
    SOLO = 'solo',
    GROUP = 'group',
}
export type PracticeType = `${EPracticeType}`;

export enum ECredentialingStatus {
    DEPOSIT_GATE = 'deposit_gate',
    IN_REVIEW = 'In review',
    DOCS_NEEDED = 'docs_needed',
    VALIDATED = 'validated',
    SUBMITTED_TO_PAYER = 'submitted_to_payer',
    PAYER_REVIEW = 'payer_review',
    PAYER_RFI = 'payer_rfi',
    APPROVED = 'approved',
    BILLING_SETUP = 'billing_setup',
    BILLING_ACTIVE = 'billing_active',
    DISAPPROVED = 'disapproved',
    PAUSED = 'paused',
    PAYMENT_PENDING = 'payment_pending',
    PANEL_CLOSED = 'panel_closed',
    MIXED = 'mixed',
}

export interface StatusMeta {
    label: string;
    badge: string;
    bucket: StatusBucket;
    icon: string;
    dict: string;
    tip: string;
}

export type TKlarityPayer = {
    id: number;
    name: string;
    state: string;
    depositCents: number;
    highDemand: number;
    status: 'ACTIVE' | 'UPCOMING';
    exclusivePayerIds: string | null;
    payerType: string | null;
    // #19：请求带 providerId 时才有。available=false 表示该 provider 的执照不能选，原因放 tooltip
    available?: boolean | null;
    unavailableReason?: string | null;
};

export enum ECredentialingFlowMode {
    INITIAL = 'initial',
    ADD = 'add',
}

export enum ECredentialingModalStep {
    SELECT_NPI = 'selectNpi',
    SELECT_GROUP_PROVIDER = 'selectGroupProvider',
    SELECT_PAYER = 'selectPayer',
    // V2 payer 触发的条件步骤链（solo INITIAL：Select payers 与 agreement 之间，按需插入）
    NP_104 = 'np104',
    GOV_PROGRAM = 'govProgram',
    IRS_LETTER = 'irsLetter',
    SERVICE_ADDRESS = 'serviceAddress',
    MEDICARE_PAYOUT = 'medicarePayout',
    OPTUM_W9 = 'optumW9',
    AETNA_FOCUS = 'aetnaFocus',
    REVIEW_AGREEMENT = 'reviewAgreement',
    CONFIRM_DEPOSITS = 'confirmDeposits',
    ADD_PAYMENT = 'addPayment',
}

// malpractice 证书到期提醒（后端按美东「今天」实时算）：NONE 不显示 / EXPIRING 0–30 天黄 / EXPIRED 红
export type TMalpracticeAlert = {
    expireDate: string | null;
    daysUntilExpiry: number | null;
    level: 'NONE' | 'EXPIRING' | 'EXPIRED';
};

export type TCredentialingProvider = {
    credentialingId: number;
    email: string;
    firstName: string;
    lastName: string;
    onboardingStatus: 'INIT' | 'IN_PROGRESS' | 'COMPLETED';
    onboardingStep?: string;
    providerId: number;
    malpracticeAlert?: TMalpracticeAlert | null;
    // 是否已有签名（10/26 补签横幅）；字段名待后端确认，undefined 视为未知、不提示
    hasSignature?: boolean | null;
};

export type TPayerSuggestionReq = {
    payerName: string;
    state: string;
    notes?: string;
};

export interface PanelStatus {
    name: string;
    status: CredentialingStatus;
    statusBucket: StatusBucket;
    statusDisplay?: string;
    state?: string;
    detail?: string;
    submitted?: string;
    updated?: string;
    contractSigned?: boolean;
    // Withdraw/Resubmit 行操作定位用（源自 TCredentialingPanelRow）
    klarityPayerId?: number;
    providerId?: number;
    // 后端原始状态（EnumCredentialingPayerStatus）；withdraw 白名单按它判定——
    // WAIT_SUBMIT 等多个原始状态映射为同一前端状态，映射后无法区分
    apiStatus?: string;
    // 后端下发：true 时展示 Resubmit 行操作
    payerActive?: boolean;
    // 源自 TCredentialingPanelRow.depositAmountCents；撤回弹窗据此决定是否提退款
    depositAmountCents?: number;
}

// Group Credentialing types
export interface AddCredentialingProviderReq {
    email: string;
    firstName: string;
    lastName: string;
}

export interface AddCredentialingProviderResp {
    created: boolean;
    providerId: number;
}

export interface OrgCredentialingPayerRef {
    klarityPayerId: number;
    payerName: string;
    state: string;
    highDemand: number;
}

export interface SaveOrgCredentialingEnrollmentReq {
    entityType?: string;
    groupNpi?: string;
    legalBusinessName?: string;
    taxId?: string;
    practiceLocation?: string;
    w9FileName?: string;
    w9FileUrl?: string;
    klarityPayerIds?: number[];
    // #6 #8 #9：按州的 group Medicaid 注册 + practice 级 PAVE。不传 / null 保留原值
    practiceRequirements?: PracticeRequirementsDto | null;
}

// Prior-credentialing (NPI pre-question) save payload.
export interface PriorYesStatePanels {
    // 2-letter state code, e.g. CA / TX
    state: string;
    // Panel keys (klarity payer ids) the provider is already credentialed with in this state
    payers: string[];
}

export interface PriorYesPanels {
    byState: PriorYesStatePanels[];
    notSure: boolean;
    otherPayers: string;
}

export interface ProviderCredentialingPriorCredentialingDto {
    // Target provider whose provider_credentialing row will be upserted
    providerId: number;
    // true = Yes, credentialed under own NPI; false = No, not yet
    priorCredentialed: boolean;
    // Sub-reason text (the selected option's label) when priorCredentialed=false
    priorNoReason?: string;
    // Already-credentialed panels, only when priorCredentialed=true
    priorYesPayers?: PriorYesPanels;
}

// One existing provider_credentialing_payer row (any status), used to hydrate a provider's
// previously saved panel selection.
export interface ProviderCredentialingPayerVo {
    // provider_credentialing_payer.id
    id: number;
    // Associated provider_appointment_billing.id (0 until billing created)
    billingId: number;
    klarityPayerId: number;
    payerName: string;
    // 2-letter state code
    payerState: string;
    providerId: number;
    // Status code, e.g. PENDING_PAYMENT
    status: string;
}

// Save one provider's panel selection as DRAFT (group flow, no billing/contract yet).
export interface AddProviderPanelsReq {
    // Target provider id
    providerId: number;
    // Selected klarity_panel.id list for this provider
    klarityPayerIds: number[];
}

export interface OrgCredentialingEnrollmentVo {
    entityType?: string;
    groupNpi?: string;
    legalBusinessName?: string;
    taxId?: string;
    practiceLocation?: string;
    w9FileName?: string;
    w9FileUrl?: string;
    onboardingStep?: string;
    payers?: OrgCredentialingPayerRef[];
    practiceRequirements?: PracticeRequirementsDto | null;
}

export type TBillingNpiType = {
    id: number;
    providerId: number;
    recordType: string;
    value: ENPIType;
    createAt: string;
    createBy: number;
    updateAt: string;
    updateBy: number;
};
// SETUP_PRACTICE
// COMPLETED
// BASIC_INFO
// LICENSES
// PRACTICE
// MALPRACTICE
// BACKGROUND
// REFERENCES
// VERIFICATION
// PRIOR_CREDENTIALING
// PAYERS_SELECTED
// CONFIRM
export enum ECredentialingStep {
    SETUP_PRACTICE = 'SETUP_PRACTICE',
    COMPLETED = 'COMPLETED',
    BASIC_INFO = 'BASIC_INFO',
    LICENSES = 'LICENSES',
    PRACTICE = 'PRACTICE',
    MALPRACTICE = 'MALPRACTICE',
    BACKGROUND = 'BACKGROUND',
    REFERENCES = 'REFERENCES',
    VERIFICATION = 'VERIFICATION',
    PRIOR_CREDENTIALING = 'PRIOR_CREDENTIALING',
    PAYERS_SELECTED = 'PAYERS_SELECTED',
    CONFIRM = 'CONFIRM',
}

export type TCredentialingOverview = {
    currentStep?: ECredentialingStep;
    billingNpiType: TBillingNpiType | null;
    providers: TCredentialingProvider[];
    states: string[];
    // 提交后处理时效：slaTier 为档位（FLASH/PRIORITY/STANDARD），slaTierDays 为对应的目标天数
    slaTier?: string;
    slaTierDays?: number;
    // 当前登录 provider 自己的证书；owner 不是 provider 时 level=NONE
    malpracticeAlert?: TMalpracticeAlert | null;
    // 当前登录 provider 是否已有签名（10/26 补签横幅）；owner 不是 provider 时不返回
    hasSignature?: boolean | null;
};

// Frontend UI types for the group credentialing flow
export interface PracticeEnrollment extends SaveOrgCredentialingEnrollmentReq {
    entityTypeOther?: string;
    // NpiRegistryBox 写进表单的 NPI 查询态（纯前端辅助字段，保存前由 stripNpiHelpers 剥掉）
    npiType?: string;
    npiChecking?: boolean;
    npiRegistryName?: { firstName?: string; lastName?: string };
    taxonomyOptions?: unknown[];
    taxonomy?: string;
}

export type TGroupSignAgreementReq = {
    completePractice: boolean;
    esignatureName: string;
    providerIds: number[];
    signedDate: string;
    // #14：providerIds 里每个 provider 都要有一条，签名必须是他本人名下的
    providerSignatures: { providerId: number; signatureId: number }[];
};

// 字段级定位协议（协议文档见 Lark《Action needed 字段级定位：ctaPayload 结构化协议》）：
// fieldKey = 集合前缀 + 字段名（如 'medicare.certificationNumber'）；判别字段按行所在集合
// 四选一——带主键的列表行传 refId、按州的 payer-requirements 传 state、披露题传 questionKey、
// additionalW9 条目传 taxId（W-9 无州维度）
export type TOptumW9RequiredEntry = {
    tin?: string;
    entityName?: string | null;
    fileUrl?: string | null;
    status?: 'PRESENT' | 'MISSING';
};

export type TCredentialingCtaPayload = {
    targetId?: number;
    field?: string;
    fieldKey?: string;
    state?: string;
    refId?: number;
    questionKey?: string;
    taxId?: string;
    // additionalW9 全量应备清单：MISSING 的条目需补录（tin/entityName 预填），PRESENT 的忽略
    optumW9Required?: TOptumW9RequiredEntry[];
};

export type TCredentialingActionItem = {
    cta?: string;
    ctaPayload?: TCredentialingCtaPayload;
    reason?: string;
    refId?: number;
    step?: string;
    type?: string;
};

export type TCredentialingPanelAction = {
    actionItems: TCredentialingActionItem[];
    credentialingPayerId: number;
    payerName: string;
    payerState: string;
    providerId: number;
    status: string;
};

export type TProviderActionGroup = {
    credential?: string;
    firstName?: string;
    lastName?: string;
    panelNeedActionCount: number;
    panels: TCredentialingPanelAction[];
    providerId: number;
    self: boolean;
    title?: string;
};

export type TPanelStatusSummary = {
    actionNeeded: number;
    inProgress: number;
    approved: number;
    live: number;
};

export type TProviderPanelGroup = {
    providerId: number;
    firstName: string;
    lastName: string;
    credential: string;
    title: string;
    self: boolean;
    panelTotal: number;
    actionNeeded: number;
    inProgress: number;
    approved: number;
    live: number;
    panels: TCredentialingPanelRow[];
};

export type TCredentialingPanelRow = {
    id: number;
    providerId: number;
    klarityPayerId: number;
    payerName: string;
    payerState: string;
    status: string;
    statusBucket: string;
    statusDisplay: string;
    providerSubmittedAt?: string;
    firstSubmittedToPayerAt?: string;
    latestSubmittedToPayerAt?: string;
    lastUpdatedAt?: string;
    // 非空 = provider 已签 payer 合同。panel 状态签前签后都是 SIGNATURE_NEEDED，
    // 这是区分「待签 / 已签」的唯一字段（tooltip 用，见 CredentialingPage/statusMeta.ts）
    payerContractSignedAt?: string;
    // true = 可重提（列表行展示 Resubmit 操作）
    payerActive?: boolean;
    // 这一行 panel 的押金快照（分）；撤回时 >0 才退款（0 = 开免押金后提交的）
    depositAmountCents?: number;
};

export type TCredentialingContractView = 'provider' | 'practice';

export type TCredentialingContractStatus =
    | 'SIGNATURE_PENDING'
    | 'SIGNATURE_NOT_RECEIVED'
    | 'SIGNATURE_VERIFYING'
    | 'SIGNATURE_AWAITING_COUNTERSIGN'
    | 'SIGNATURE_EXECUTED'
    | 'SIGNATURE_WITHDRAWN_BY_PAYER'
    | 'SIGNATURE_LINK_EXPIRED';

export type TCredentialingCoveredPanel = {
    panelId: number;
    providerId: number;
    providerName?: string;
    payerName?: string;
    payerState?: string;
    payerResults?: string;
    payerEffectiveAt?: string;
    panelStatus?: string;
};

export type TCredentialingContractBanner = {
    contractId?: number;
    panelId: number;
    providerId: number;
    providerName?: string;
    coveredPanels: TCredentialingCoveredPanel[];
    payerName: string;
    payerState: string;
    agreementName?: string;
    status: TCredentialingContractStatus | 'SIGNATURE_NEEDED';
    statusDisplay: string;
    signByAt?: string;
    canSign: boolean;
    signUrl?: string;
};

export type TCredentialingContractFile = {
    kind: string;
    fileName: string;
    downloadUrl: string;
};

export type TCredentialingContract = {
    contractId: number;
    panelId: number;
    providerId: number;
    providerName?: string;
    coveredPanels: TCredentialingCoveredPanel[];
    payerName: string;
    payerState: string;
    agreementName?: string;
    status: TCredentialingContractStatus;
    statusDisplay: string;
    signByAt?: string;
    canSign: boolean;
    selfReportedSignedAt?: string;
    executedAt?: string;
    payerEffectiveAt?: string;
    payerResults?: string;
    files: TCredentialingContractFile[];
};
